import React, { useMemo } from "react";
import { BootstrapSizes, Button, ButtonColors } from "@talentech/components";
import {
  FormJson,
  QuestionType,
} from "../types/feedback.types";
import { getVisibleQuestions } from "../utils/formUtils";
import QuestionStarRating from "./QuestionTypes/QuestionStarRating";
import { useForm, SubmitHandler, FormProvider } from "react-hook-form";
import NPSRating from "./QuestionTypes/NPSRating";
import QuestionComment from "./QuestionTypes/QuestionComment";
import { useTranslation } from "react-i18next";

interface FeedbackFormRendererProps {
  formJson: FormJson | null;
  currentLanguageId?: number;
  className?: string;
  onSubmit?: (responses: Record<string, string | number>) => void;
}

/**
 * FeedbackFormRenderer
 * Renders dynamic feedback forms with multi-language support
 * Supports NPS, Rating (stars), and Free Text question types
 */
const FeedbackFormRenderer: React.FC<FeedbackFormRendererProps> = ({
  formJson,
  className,
  onSubmit,
}) => {
  const { t } = useTranslation();

  const visibleQuestions = useMemo(
    () => getVisibleQuestions(formJson),
    [formJson]
  );

  const methods = useForm<Record<string, string | number>>({
    defaultValues: {},
  });

  const onHandleSubmit: SubmitHandler<Record<string, string | number>> = (
    data
  ) => {
    onSubmit?.(data);
  };

  if (!formJson) {
    return <div className={className}>{t("Form.Errors.NoFormDataProvided")}</div>;
  }

  return (
    <>
      <div className="p-4 rounded-3 bg-white">
        <p className="text-muted fs-12 mb-0 d-flex align-items-center">
          <i className="fa-sharp fa-light fa-circle-info me-2 fs-5"></i>
          {t("Form.Info.Anonymous")}
        </p>
      </div>

      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit(onHandleSubmit)}
          className="d-flex flex-column gap-4"
          noValidate
        >
          {visibleQuestions.map((q) => {
            const typeId = q.typeId ?? QuestionType.FREE_TEXT;

            if (typeId === QuestionType.NPS) {
              return <NPSRating key={q.id} {...q} />;
            }
            if (typeId === QuestionType.RATING) {
              return <QuestionStarRating key={q.id} {...q} />;
            }

            return <QuestionComment key={q.id} {...q} />;
          })}

          <Button
            type="submit"
            color={ButtonColors.PRIMARY}
            size={BootstrapSizes.XLARGE}
            block={true}
            className=""
            label={t("Button.Send")}
            aria-label={t("Button.Send")}
          />
        </form>
      </FormProvider>
    </>
  );
};

export default FeedbackFormRenderer;
